import {
  setGatewayControllers,
  updateCurrentSiteControllers,
  reconcileSites,
  reconcileGateways,
  reconcileAlerts,
  reconcileControllers,
} from "./index";
import api from "services/backendService";
import utils from "utils";

export const setControllersByGatewayGuidAsync = (gatewayGuid) => {
  return async (dispatch) => {
    try {
      const response = await api.getDevicesOnGateway(gatewayGuid);
      const controllers = Object.keys(response.byId).map((deviceGuid) => ({
        deviceGuid,
        model: response.byId[deviceGuid].model,
        friendlyName: response.byId[deviceGuid]?.friendlyName,
        isDongleController: response.byId[deviceGuid].isDongleController,
      }));

      dispatch(setGatewayControllers({ gatewayGuid, controllers }));
      dispatch(updateCurrentSiteControllers({...response, gatewayGuid}));
    } catch (error) {
      utils.debug(
        "error",
        `Error in setControllersByGatewayGuidAsync: ${error}`
      );
    }
  };
};

/**
 * Background reconciliation of the core store after a SignalR reconnect.
 *
 * Fetches an authoritative REST snapshot and dispatches RECONCILE_* actions
 * (merge-by-id, reference-preserving) instead of the destructive SET_* actions
 * used by the page-mount loadData. This restores consistency for any messages
 * lost during the connection gap WITHOUT the "fresh login" effect: unchanged
 * entities keep their reference, so the UI does not flash, reset filters, or
 * discard the user's in-progress work.
 *
 * Each slice is reconciled independently so one slow/failed call does not block
 * the others, and nothing is ever blanked to a placeholder.
 */
export const reconcileDashboardStateAsync = (orgGuid, selectedSiteGuid) => {
  return async (dispatch) => {
    const tasks = [
      (async () => {
        const sites = await api.getSiteInitialState(null, false, orgGuid);
        dispatch(reconcileSites(sites));
      })(),
      (async () => {
        const gateways = await api.getGatewayInitialState(
          orgGuid,
          null,
          null,
          false
        );
        dispatch(reconcileGateways(gateways));
      })(),
      (async () => {
        const alerts = await api.getAlertInitialState();
        dispatch(reconcileAlerts(alerts));
      })(),
    ];

    if (selectedSiteGuid) {
      tasks.push(
        (async () => {
          const controllers = await api.getDevicesOnSite(selectedSiteGuid, orgGuid);
          dispatch(reconcileControllers(controllers));
        })()
      );
    }

    const results = await Promise.allSettled(tasks);
    for (const result of results) {
      if (result.status === "rejected") {
        utils.debug(
          "error",
          `Error in reconcileDashboardStateAsync: ${result.reason}`
        );
      }
    }
  };
};

/**
 * Lightweight alerts-only reconcile.
 *
 * When a gateway comes back online the backend does not reliably stream an alert
 * OFF for its connectivity alarms (notably "No Internet"), leaving a stale active
 * alert in `alerts.byId` that every count/highlight derives from. Refetch the
 * authoritative alert snapshot and merge it (reference-preserving) so stale
 * alerts clear without waiting for a SignalR reconnect or a browser-tab focus
 * reconcile. Errors are swallowed (best-effort, never blocks the stream).
 */
export const reconcileAlertsAsync = () => {
  return async (dispatch) => {
    try {
      const alerts = await api.getAlertInitialState();
      dispatch(reconcileAlerts(alerts));
    } catch (error) {
      utils.debug("error", `Error in reconcileAlertsAsync: ${error}`);
    }
  };
};
